// Use empty string to use relative URLs (Vite proxy) or fallback to localhost
const API_URL = import.meta.env.VITE_API_URL || ''

const getAuthHeaders = () => {
  const token = localStorage.getItem('auth_token')
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  }
}

// Migration CRUD
export const createMigration = async (migrationData) => {
  const response = await fetch(`${API_URL}/api/v1/migrations/`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(migrationData)
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to create migration')
  }

  return response.json()
}

export const listMigrations = async (tenancyId) => {
  const response = await fetch(`${API_URL}/api/v1/migrations/?tenancy_id=${tenancyId}`, {
    headers: getAuthHeaders()
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to fetch migrations')
  }

  return response.json()
}

export const getMigration = async (migrationId) => {
  const response = await fetch(`${API_URL}/api/v1/migrations/${migrationId}`, {
    headers: getAuthHeaders()
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to fetch migration')
  }

  return response.json()
}

export const updateMigration = async (migrationId, updates) => {
  const response = await fetch(`${API_URL}/api/v1/migrations/${migrationId}`, {
    method: 'PATCH',
    headers: getAuthHeaders(),
    body: JSON.stringify(updates)
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to update migration')
  }

  return response.json()
}

export const deleteMigration = async (migrationId) => {
  const response = await fetch(`${API_URL}/api/v1/migrations/${migrationId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to delete migration')
  }
}

// Migration Config
export const createOrUpdateMigrationConfig = async (migrationId, configData) => {
  const response = await fetch(`${API_URL}/api/v1/migrations/${migrationId}/config`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(configData)
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to save migration config')
  }

  return response.json()
}

export const getMigrationConfig = async (migrationId) => {
  const response = await fetch(`${API_URL}/api/v1/migrations/${migrationId}/config`, {
    headers: getAuthHeaders()
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to fetch migration config')
  }

  return response.json()
}

// Odoo Validation
export const validateOdooCredentials = async (credentials) => {
  const body = {
    odoo_url: credentials.odoo_url,
    username: credentials.username,
    password: credentials.password
  }
  // Only include database if provided (otherwise backend will auto-detect)
  if (credentials.database) {
    body.database = credentials.database
  }

  const response = await fetch(`${API_URL}/api/v1/odoo/validate`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(body)
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to validate Odoo credentials')
  }

  return response.json()
}

// Migration Files
export const addMigrationFile = async (migrationId, fileData) => {
  const response = await fetch(`${API_URL}/api/v1/migrations/${migrationId}/files`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(fileData)
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to add file')
  }

  return response.json()
}

export const listMigrationFiles = async (migrationId) => {
  const response = await fetch(`${API_URL}/api/v1/migrations/${migrationId}/files`, {
    headers: getAuthHeaders()
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to fetch files')
  }

  return response.json()
}

export const deleteMigrationFile = async (migrationId, fileId) => {
  const response = await fetch(`${API_URL}/api/v1/migrations/${migrationId}/files/${fileId}`, {
    method: 'DELETE',
    headers: getAuthHeaders()
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to delete file')
  }
}

export const getFileDownloadUrl = async (migrationId, fileId) => {
  const response = await fetch(`${API_URL}/api/v1/migrations/${migrationId}/files/${fileId}/download`, {
    headers: getAuthHeaders()
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to get download URL')
  }

  return response.json()
}

// Multipart Upload API
export const createMultipartUpload = async (migrationId, fileInfo) => {
  const response = await fetch(`${API_URL}/api/v1/migrations/${migrationId}/files/multipart/create`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(fileInfo)
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to create multipart upload')
  }

  return response.json()
}

export const getPresignedPartUrls = async (migrationId, uploadId, storageKey, partNumbers) => {
  const response = await fetch(`${API_URL}/api/v1/migrations/${migrationId}/files/multipart/presign-parts`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      upload_id: uploadId,
      storage_key: storageKey,
      part_numbers: partNumbers
    })
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to get presigned URLs')
  }

  return response.json()
}

export const completeMultipartUpload = async (migrationId, uploadId, storageKey, parts, fileMetadata) => {
  const response = await fetch(`${API_URL}/api/v1/migrations/${migrationId}/files/multipart/complete`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      upload_id: uploadId,
      storage_key: storageKey,
      parts: parts.map(p => ({
        partNumber: p.PartNumber,
        etag: p.ETag
      })),
      file_name: fileMetadata.file_name,
      file_type: fileMetadata.file_type,
      file_size: fileMetadata.file_size,
      content_type: fileMetadata.content_type,
      description: fileMetadata.description
    })
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to complete upload')
  }

  return response.json()
}

export const abortMultipartUpload = async (migrationId, uploadId, storageKey) => {
  const response = await fetch(`${API_URL}/api/v1/migrations/${migrationId}/files/multipart/abort`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify({
      upload_id: uploadId,
      storage_key: storageKey
    })
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to abort upload')
  }
}

// Tenancy/User helpers
export const listUserTenancies = async () => {
  const response = await fetch(`${API_URL}/api/v1/tenancies/my-tenancies`, {
    headers: getAuthHeaders()
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to fetch tenancies')
  }

  return response.json()
}

export const getTenancyRelationships = async (tenancyId) => {
  const response = await fetch(`${API_URL}/api/v1/tenancies/${tenancyId}/relationships`, {
    headers: getAuthHeaders()
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.detail || 'Failed to fetch relationships')
  }

  return response.json()
}
